import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { extend, useThree, useFrame } from "@react-three/fiber";
import { SparkRenderer, SplatMesh, dyno } from "@sparkjsdev/spark";
import { OrbitControls, Html } from '@react-three/drei';

extend({ SparkRenderer, SplatMesh });

export function DebugOrbitPose() {
  const { camera } = useThree();
  const controlsRef = useRef();

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key.toLowerCase() !== "p") return;

      const c = controlsRef.current;
      if (!c) return;

      const pos = camera.position;
      const t = c.target;

      console.log("camera.position =", [pos.x, pos.y, pos.z].map(n => +n.toFixed(4)));
      console.log("controls.target =", [t.x, t.y, t.z].map(n => +n.toFixed(4)));
      console.log("camera.zoom =", camera.zoom);
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [camera]);

  return (
    <OrbitControls
      ref={controlsRef}
      target={[0, 0, 1]}
      enableDamping
      autoRotate
      autoRotateSpeed={0.2}
    />
  );
}

function CameraSetup() {
  const { camera } = useThree();

  useEffect(() => {
    camera.position.set(25.0, 0.5, -10.0);
  }, [camera]);

  return null;
}

const MORPH_TRANSITION_SPEED = 5;
const MORPH_SCATTER_RADIUS = 50;

function createMorphModifier(progress, objectIndex, randomRadius) {
  const morphDyn = new dyno.Dyno({
    inTypes: {
      gsplat: dyno.Gsplat,
      progress: "float",
      objectIndex: "int",
      randomRadius: "float",
    },
    outTypes: { gsplat: dyno.Gsplat },
    globals: () => [
      dyno.unindent(`
        vec3 morphHash3(int n) {
          float x = float(n);
          return fract(sin(vec3(x, x + 1.0, x + 2.0)) * 43758.5453123);
        }
        float morphEase(float x) { return x * x * (3.0 - 2.0 * x); }
        vec3 morphRandPos(int splatIndex, float radius) {
          vec3 h = morphHash3(splatIndex);
          float theta = 6.28318530718 * h.x;
          float r = radius * sqrt(h.y);
          return vec3(r * cos(theta), 0.0, r * sin(theta));
        }
      `),
    ],
    statements: ({ inputs, outputs }) =>
      dyno.unindentLines(`
        ${outputs.gsplat} = ${inputs.gsplat};
        float p = ${inputs.progress};
        int idx = ${inputs.objectIndex};

        vec3 rp = morphRandPos(int(${inputs.gsplat}.index), ${inputs.randomRadius});
        vec3 rpMid = mix(${inputs.gsplat}.center, rp, 0.7);

        float alpha = 0.0;
        vec3 pos = ${inputs.gsplat}.center;
        vec3 origScale = ${inputs.gsplat}.scales;
        vec3 small = ${inputs.gsplat}.scales * 0.2;

        if (idx == 0) {
          if (p <= 0.5) {
            float s = morphEase(p / 0.5);
            alpha = 1.0 - s * 0.5;
            pos = mix(${inputs.gsplat}.center, rpMid, s);
            ${outputs.gsplat}.scales = mix(origScale, small, s);
          } else {
            alpha = 0.0;
            pos = rpMid;
            ${outputs.gsplat}.scales = small;
          }
        } else {
          if (p <= 0.5) {
            alpha = 0.0;
            pos = rpMid;
            ${outputs.gsplat}.scales = small;
          } else {
            float s = morphEase((p - 0.5) / 0.5);
            alpha = max(s, 0.5);
            pos = mix(rpMid, ${inputs.gsplat}.center, s);
            ${outputs.gsplat}.scales = mix(small, origScale, s);
          }
        }

        ${outputs.gsplat}.center = pos;
        ${outputs.gsplat}.rgba.a = ${inputs.gsplat}.rgba.a * alpha;
      `),
  });

  return dyno.dynoBlock(
    { gsplat: dyno.Gsplat },
    { gsplat: dyno.Gsplat },
    ({ gsplat }) => ({
      gsplat: morphDyn.apply({
        gsplat,
        progress,
        objectIndex,
        randomRadius,
      }).gsplat,
    }),
  );
}

export default function SplatComponent({ setSplatToggleFn }) {
  const [splatUrl1, setSplatUrl1] = useState(null);
  const [splatUrl2, setSplatUrl2] = useState(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [showingB, setShowingB] = useState(false);

  const gl = useThree((state) => state.gl);
  const meshRef1 = useRef(null);
  const meshRef2 = useRef(null);

  const progressRef = useRef(dyno.dynoFloat(0));
  const targetProgressRef = useRef(0);
  const modifiersAttachedRef = useRef(false);

  const isLoaded = loadedCount >= 2;

  const sparkRendererArgs = useMemo(() => {
    return [{
      renderer: gl,
      maxStdDev: Math.sqrt(8),
      enable2DGS: true,
    }];
  }, [gl]);

  // In development, serve .sog files directly from public/ to avoid needing
  // the CloudFront signing function. In production, fetch signed URLs via
  // the Netlify function so assets are served through the CloudFront CDN.
  useEffect(() => {
    let cancelled = false;

    async function fetchUrls() {
      try {
        if (process.env.NODE_ENV === "development") {
          if (!cancelled) {
            setSplatUrl1("/me_winter_pruned_edited.sog");
            setSplatUrl2("/me_winter_pruned_edited_2_final.sog");
          }
          return;
        }

        const [res1, res2] = await Promise.all([
          fetch("/.netlify/functions/sign-splat?path=/me_winter_pruned_edited.sog"),
          fetch("/.netlify/functions/sign-splat?path=/me_winter_pruned_edited_2_final.sog"),
        ]);

        if (!res1.ok || !res2.ok) throw new Error("Failed to fetch signed URLs");

        const { signedUrl: url1 } = await res1.json();
        const { signedUrl: url2 } = await res2.json();

        if (!cancelled) {
          setSplatUrl1(url1);
          setSplatUrl2(url2);
        }
      } catch (err) {
        console.error("Failed to fetch signed splat URLs:", err);
      }
    }

    fetchUrls();
    return () => { cancelled = true; };
  }, []);

  const handleLoad = useCallback(() => {
    setLoadedCount(prev => prev + 1);
  }, []);

  const splatMeshArgs1 = useMemo(() => {
    if (!splatUrl1) return null;
    return [{ url: splatUrl1, onLoad: handleLoad }];
  }, [splatUrl1, handleLoad]);

  const splatMeshArgs2 = useMemo(() => {
    if (!splatUrl2) return null;
    return [{ url: splatUrl2, onLoad: handleLoad }];
  }, [splatUrl2, handleLoad]);

  useEffect(() => {
    if (!isLoaded || modifiersAttachedRef.current) return;

    const mesh1 = meshRef1.current;
    const mesh2 = meshRef2.current;
    if (!mesh1 || !mesh2) return;

    const radiusDyn = dyno.dynoFloat(MORPH_SCATTER_RADIUS);

    mesh1.worldModifier = createMorphModifier(
      progressRef.current, dyno.dynoInt(0), radiusDyn
    );
    mesh1.updateGenerator();

    mesh2.worldModifier = createMorphModifier(
      progressRef.current, dyno.dynoInt(1), radiusDyn
    );
    mesh2.updateGenerator();

    modifiersAttachedRef.current = true;
  }, [isLoaded]);

  useFrame((_, delta) => {
    const mesh1 = meshRef1.current;
    const mesh2 = meshRef2.current;
    if (!mesh1 || !mesh2 || !modifiersAttachedRef.current) return;

    const current = progressRef.current.value;
    const target = targetProgressRef.current;
    const diff = target - current;

    if (Math.abs(diff) > 0.001) {
      const step = Math.sign(diff) * Math.min(Math.abs(diff), delta * MORPH_TRANSITION_SPEED);
      progressRef.current.value += step;
      mesh1.updateVersion();
      mesh2.updateVersion();
    } else if (current !== target) {
      progressRef.current.value = target;
      mesh1.updateVersion();
      mesh2.updateVersion();
    }
  });

  const handleToggle = useCallback(() => {
    setShowingB(prev => {
      targetProgressRef.current = prev ? 0 : 1;
      return !prev;
    });
  }, []);

  useEffect(() => {
    if (isLoaded && setSplatToggleFn) {
      setSplatToggleFn(() => handleToggle);
    }
    return () => {
      if (setSplatToggleFn) setSplatToggleFn(null);
    };
  }, [isLoaded, handleToggle, setSplatToggleFn]);

  if (!splatMeshArgs1 || !splatMeshArgs2) return null;

  return (
    <>
      <CameraSetup />
      <OrbitControls
        target={[0, 0, 0]}
        autoRotate
        autoRotateSpeed={0.2}
        enableDamping
        minDistance={5.0}
        maxDistance={175.0}
        minPolarAngle={0.2}
        maxPolarAngle={Math.PI / 2}
      />
      {!isLoaded && (
        <Html center>
          <h1>loading...</h1>
        </Html>
      )}
      <sparkRenderer args={sparkRendererArgs}>
        <group rotation={[Math.PI, 0, 0]} visible={isLoaded}>
          <splatMesh ref={meshRef1} args={splatMeshArgs1} />
          <splatMesh ref={meshRef2} args={splatMeshArgs2} />
        </group>
      </sparkRenderer>
    </>
  );
}
